import type {
  ExpressionNodeType,
  ExpressionPayloadType,
} from "@intelligems/ig-types";

export type operator = "or" | "and";

export const expressionListToInfix = (
  expressionsList: ExpressionPayloadType[],
  ingroupOperator: operator = "or",
  outgroupOperator: operator = "and"
): ExpressionNodeType[] => {
  const infix: ExpressionNodeType[] = [];
  for (let i = 0; i < expressionsList.length; i++) {
    const queries = expressionsList[i].queries;
    if (!queries.length) {
      continue;
    }

    infix.push({ operator: "start_paren" });
    for (let j = 0; j < queries.length; j++) {
      const query = queries[j];
      infix.push({ query });
      if (j !== queries.length - 1) {
        infix.push({ operator: ingroupOperator });
      }
    }
    infix.push({ operator: "end_paren" });
    if (i !== expressionsList.length - 1) {
      infix.push({ operator: outgroupOperator });
    }
  }
  return infix;
};

export const infixToExpressionList = (
  infix: ExpressionNodeType[],
  outgroupOperator: operator = "and"
): ExpressionPayloadType[] => {
  const expressionsList: ExpressionPayloadType[] = [];
  let payload: ExpressionPayloadType | null = null;
  for (let i = 0; i < infix.length; i++) {
    const curr = infix[i];
    if (curr.query) {
      if (!payload)
        payload = {
          type: curr.query.type,
          queries: [curr.query],
        };
      else payload.queries.push(curr.query);
    } else if (curr.operator === outgroupOperator && payload) {
      expressionsList.push(payload);
      payload = null;
    }
  }
  if (payload) expressionsList.push(payload);
  return expressionsList;
};

export const postfixToInfix = (
  postfix: ExpressionNodeType[]
): ExpressionNodeType[] => {
  const stack: ExpressionNodeType[][] = [[]];
  for (let i = 0; i < postfix.length; i++) {
    const curr = postfix[i];
    if (postfix[i].query) {
      stack.push([curr]);
    } else {
      const op1 = stack.pop();
      const op2 = stack.pop();
      if (op1 !== undefined && op2 !== undefined) {
        stack.push([
          { operator: "start_paren" },
          ...op2,
          postfix[i],
          ...op1,
          { operator: "end_paren" },
        ]);
      }
    }
  }
  return stack[stack.length - 1];
};

export const infixToPostfix = (
  infix: ExpressionNodeType[]
): ExpressionNodeType[] => {
  const postfix: ExpressionNodeType[] = [];
  const stack: ExpressionNodeType[] = [];
  for (const token of infix) {
    if (token.query) {
      postfix.push(token);
    } else if (token.operator === "start_paren") {
      stack.push(token);
    } else if (token.operator === "end_paren") {
      while (
        stack.length &&
        stack[stack.length - 1].operator !== "start_paren"
      ) {
        postfix.push(stack.pop()!);
      }
      stack.pop();
    } else {
      while (
        stack.length &&
        (stack[stack.length - 1].operator === "and" ||
          stack[stack.length - 1].operator === "or")
      ) {
        postfix.push(stack.pop()!);
      }
      stack.push(token);
    }
  }
  while (stack.length) {
    postfix.push(stack.pop()!);
  }
  return postfix;
};

export const postfixToExpressionList = (
  postfix: ExpressionNodeType[],
  mode: "audience" | "pageTargeting" = "audience"
): ExpressionPayloadType[] => {
  let outgroupOperator: operator = "and";
  if (mode == "pageTargeting") {
    outgroupOperator = "or";
  }

  const infix: ExpressionNodeType[] = postfixToInfix(postfix);
  const expressionsList: ExpressionPayloadType[] = infixToExpressionList(
    infix,
    outgroupOperator
  );
  return expressionsList;
};

export const expressionListToPostFix = (
  expressionsList: ExpressionPayloadType[],
  mode: "audience" | "pageTargeting" = "audience"
): ExpressionNodeType[] => {
  let { ingroupOperator, outgroupOperator } = {
    ingroupOperator: "or" as operator,
    outgroupOperator: "and" as operator,
  };
  if (mode === "pageTargeting") {
    ingroupOperator = "and";
    outgroupOperator = "or";
  }
  const infix: ExpressionNodeType[] = expressionListToInfix(
    expressionsList,
    ingroupOperator,
    outgroupOperator
  );
  const postfix: ExpressionNodeType[] = infixToPostfix(infix);
  return postfix;
};
